import {Link} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {ChevronRight} from 'lucide-react';
import {DsAvatar} from 'ch3-ui-lib';
import {selectUserId} from '../../store/features/Auth/authSlice';
import {useFetchCurrentUserQuery} from '../../store/features/Users/userService';
import {useGetStatusesQuery} from '../../store/features/Team/teamService';
import {useGetUserMsisdnsQuery} from '../../store/features/Msisdns/msisdnService';
import {useIsEnsEnabledQuery} from '../../services/chatService';
import {hexToEmoji} from '../../components/Emoji/emoji';
import {getUserStatusName} from '../../utilis/getStatus';
import {SettingsCard, SettingsGroup, SettingsLayout, SettingsPage} from './SettingsLayout';
import {SettingsRow} from './SettingsRow';
import {allAllowed, useBrowserPermission} from './browserPermissions';

const GeneralSettings = () => {
    const userId = useSelector(selectUserId);
    const {data: user} = useFetchCurrentUserQuery(userId, {skip: !userId});
    const {data: statuses} = useGetStatusesQuery({users: user?.userId ? [user.userId] : []}, {skip: !user?.userId});
    const {data: lines} = useGetUserMsisdnsQuery({userId, phrase: ''}, {skip: !userId});
    const {data: isEnsEnabled} = useIsEnsEnabledQuery(undefined, {skip: !userId});
    const microphone = useBrowserPermission('microphone');
    const notifications = useBrowserPermission('notifications');

    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    const status = statuses?.[0];
    const customStatus = status?.customUserStatus?.name;

    return (
        <SettingsLayout title="Settings">
            <SettingsPage>
                <SettingsGroup title="Account" description="Your profile and status in this workspace.">
                    <Link to="/profile" className="flex items-center gap-4 rounded-card bg-surface p-4 hover:bg-surface-muted" data-testid="settings-account-card">
                        <DsAvatar size={64} name={name} src={user?.avatarUrl || undefined}/>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-title text-ink">{name || 'Your profile'}</div>
                            {user?.email && <div className="truncate text-footnote text-ink-muted">{user.email}</div>}
                        </div>
                        {status && (
                            <span className="inline-flex h-[26px] shrink-0 items-center gap-2 rounded-chip bg-surface-muted px-2.5 text-footnote font-medium text-ink">
                                {customStatus && status.customUserStatus?.icon
                                    ? <>{hexToEmoji(status.customUserStatus.icon, '!w-4 !h-4')}{customStatus}</>
                                    : getUserStatusName(status)}
                            </span>
                        )}
                        <ChevronRight size={16} aria-hidden="true" className="!p-0 shrink-0 !text-ink-muted"/>
                    </Link>
                </SettingsGroup>

                <SettingsGroup title="Calls" description="How calls reach you.">
                    <SettingsCard>
                        <SettingsRow
                            title="Phone numbers"
                            description="Numbers you can call from"
                            to="/phoneNumbers"
                            trailing={<span className="text-callout font-medium text-ink-muted">{lines?.data?.length ?? 0}</span>}
                        />
                        <SettingsRow title="Audio" description="Microphone and speaker for calls" to="/settings/audio"/>
                    </SettingsCard>
                </SettingsGroup>

                <SettingsGroup title="Notifications" description="Alerts from this browser.">
                    <SettingsCard>
                        <SettingsRow
                            title="Browser permissions"
                            description="Microphone and notifications for incoming calls"
                            to="/settings/permissions"
                            trailing={microphone.state && notifications.state && (
                                <span className={`text-footnote font-semibold ${allAllowed(microphone.state, notifications.state) ? 'text-success-ink' : 'text-warning-ink'}`}>
                                    {allAllowed(microphone.state, notifications.state) ? 'All allowed' : 'Needs attention'}
                                </span>
                            )}
                        />
                        {isEnsEnabled === true && (
                            <SettingsRow title="Notification history" description="View received alerts and confirmations" to="/ens/my-alerts"/>
                        )}
                    </SettingsCard>
                </SettingsGroup>
            </SettingsPage>
        </SettingsLayout>
    );
};

export default GeneralSettings;
